import { AttachableDocument } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	ResizableLayout,
} from "@dune/ui";
import { useMemo } from "react";
import { useGetConstructionSiteDocuments } from "../../../../api/queries/constructionSites.queries";
import { TemplateSelectionDialog } from "../../../../components/email/TemplateSelectionDialog";
import SignatureEditorDialog from "../../../../email/SignatureEditorDialog";
import { EmailComposerForm } from "../../../../email/components/EmailComposerForm";
import { useEmailComposer } from "../../../../hooks/useEmailComposer";
import { useImageUpload } from "../../../../hooks/useImageUpload";
import type { EmailComposerPanelProps } from "../../../../types/panel.types";

export const ConstructionSiteEmailComposerPanel = ({
	initialData,
	draftId,
	disabled = false,
	replyTo,
	entityId,
	entityType = "CONSTRUCTION_SITE",
	onClose,
}: EmailComposerPanelProps) => {
	const { uploadImage } = useImageUpload();

	const {
		control,
		watch,
		attachments,
		showSaveDraftPrompt,
		setShowSaveDraftPrompt,
		showCcBcc,
		setShowCcBcc,
		showTemplateDialog,
		setShowTemplateDialog,
		isSignatureDialogOpen,
		setIsSignatureDialogOpen,
		toSearch,
		ccSearch,
		bccSearch,
		templates,
		isLoadingTemplates,
		handleTemplateSelection,
		handleAttachmentAdd,
		handleAttachmentRemove,
		handleExistingDocumentsAdd,
		handleSendEmail,
		handleSaveDraft,
		handleDiscardDraft,
		getHandleEmailSearch,
		sendEmailMutation,
	} = useEmailComposer({
		initialData,
		draftId,
		entityId,
		entityType,
		replyTo,
		onClose,
		mapAttachmentToPayload: (att) => ({
			constructionSiteDocumentId: att.entityDocumentId,
		}),
	});

	const { data: constructionSiteDocuments, isLoading: isLoadingDocuments } =
		useGetConstructionSiteDocuments(entityId, undefined, {
			enabled: !!entityId,
		});

	const constructionSiteDocumentsAsAttachableDocuments: AttachableDocument[] =
		useMemo(() => {
			if (!constructionSiteDocuments?.data) return [];
			return constructionSiteDocuments.data.map(
				(doc): AttachableDocument => ({
					id: doc.id.toString(),
					name: doc.name,
					bucket: doc.bucket,
					key: doc.key,
					mimeType: doc.mimeType,
					type: doc.type,
					createdAt: new Date(doc.createdAt),
					updatedAt: new Date(doc.updatedAt),
					status: doc.status,
				}),
			);
		}, [constructionSiteDocuments]);

	const handleConstructionSiteDocumentsAdd = async (
		documents: AttachableDocument[],
	) => {
		await handleExistingDocumentsAdd({
			documents,
			originalDocuments: constructionSiteDocuments?.data,
			findOriginal: (docs, id) => docs.find((doc) => doc.id.toString() === id),
			extractInfo: (doc) => ({
				bucket: doc.bucket,
				key: doc.key,
				name: doc.name,
				mimeType: doc.mimeType,
			}),
			extractId: (doc) => doc.id.toString(),
			entityName: "construction site",
		});
	};

	return (
		<>
			<ResizableLayout.PanelContent className="p-4">
				<EmailComposerForm
					control={control}
					watch={watch}
					disabled={disabled}
					attachments={attachments}
					showCcBcc={showCcBcc}
					setShowCcBcc={setShowCcBcc}
					toSearch={toSearch}
					ccSearch={ccSearch}
					bccSearch={bccSearch}
					getHandleEmailSearch={getHandleEmailSearch}
					handleAttachmentAdd={handleAttachmentAdd}
					handleAttachmentRemove={handleAttachmentRemove}
					existingDocuments={constructionSiteDocumentsAsAttachableDocuments}
					isLoadingDocuments={isLoadingDocuments}
					onExistingDocumentsAdd={handleConstructionSiteDocumentsAdd}
					onImageUpload={uploadImage}
					onTemplateButtonClick={() => setShowTemplateDialog(true)}
					onSignatureButtonClick={() => setIsSignatureDialogOpen(true)}
					onSendClick={handleSendEmail}
					sendEmailMutation={sendEmailMutation}
					isLoadingTemplates={isLoadingTemplates}
				/>
			</ResizableLayout.PanelContent>

			<Dialog
				open={showSaveDraftPrompt}
				onOpenChange={(open: boolean) => {
					if (!open) {
						setShowSaveDraftPrompt(false);
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Sauvegarder le brouillon ?</DialogTitle>
						<DialogDescription>
							Voulez-vous sauvegarder ce message en tant que brouillon ?
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="outline" onClick={handleDiscardDraft}>
							Supprimer
						</Button>
						<Button onClick={handleSaveDraft}>Sauvegarder</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<SignatureEditorDialog
				open={isSignatureDialogOpen}
				onOpenChange={setIsSignatureDialogOpen}
			/>

			<TemplateSelectionDialog
				open={showTemplateDialog}
				onOpenChange={setShowTemplateDialog}
				templates={templates || []}
				isLoading={isLoadingTemplates}
				onSelectTemplate={handleTemplateSelection}
			/>
		</>
	);
};
